// dashboard/src/components/ViewProjectModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { format } from 'date-fns';
import { Briefcase, MapPin, FileText, Tag, Calendar, User, Clock, Info, Users, ChevronDown, ChevronUp, X, DollarSign, History } from "lucide-react"; // Added History
import axios from "axios";

// A reusable component to display a detail item with an icon.
const DetailItem = ({ icon, label, value, isLongText = false }) => {
    const displayValue = Array.isArray(value) ? value.join(", ") : value;
    const finalValue = displayValue || "N/A";
    const valueClass = finalValue === "N/A" ? "text-gray-400 font-normal" : "font-semibold text-gray-800";
    
    const getNestedValue = (val) => {
        if (typeof val === 'object' && val !== null) {
            return `${val.firstName} ${val.lastName}` || 'N/A';
        }
        return val;
    };

    const finalDisplayValue = getNestedValue(value) || "N/A";
    const finalValueClass = finalDisplayValue === "N/A" ? "text-gray-400 font-normal" : "font-semibold text-gray-800";
    
    return (
        <div className="flex items-start py-2">
            <div className="text-orange-500 mr-4 mt-1">{icon}</div>
            <div className="flex-1">
                <p className="text-sm text-gray-500">{label}</p>
                <p className={`${finalValueClass} ${isLongText ? 'mt-1' : ''}`}>{finalDisplayValue}</p>
            </div>
        </div>
    );
};

// Collapsible Section component
const Section = ({ title, icon, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden bg-white">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-5 py-3 bg-gray-50 font-semibold text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center space-x-2">
                    <span className="text-orange-500">{icon}</span>
                    <span>{title}</span>
                </div>
                <span className="text-gray-500 text-lg">{isOpen ? <ChevronDown /> : <ChevronUp />}</span>
            </button>
            {isOpen && <div className="p-5">{children}</div>}
        </div>
    );
};

const ViewProjectModal = ({ isOpen, onClose, projectId }) => {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const modalRef = useRef(null);

    // Handle click outside modal to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    const fetchProjectDetails = useCallback(async () => {
        if (!projectId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            const API_URL = "https://ims.holdthechild.net";
            const response = await axios.get(`${API_URL}/api/projects/${projectId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            setProject(response.data.data);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch project details.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (isOpen) {
            fetchProjectDetails();
        }
    }, [isOpen, fetchProjectDetails]);

    if (!isOpen) return null;

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                    <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                        <h2 className="text-2xl font-bold text-orange-600">Project Profile</h2>
                        <button type="button" onClick={onClose} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                        <svg className="animate-spin h-8 w-8 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-3 text-lg font-semibold">Loading project details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                    <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                        <h2 className="text-2xl font-bold text-orange-600">Project Profile</h2>
                        <button type="button" onClick={onClose} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                    </div>
                    <div className="flex-1 flex items-center justify-center text-red-500">
                        <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <p className="text-xl font-semibold mb-2">Error Loading Project</p>
                            <p className="mb-4">{error}</p>
                            <button onClick={onClose} className="px-6 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!project) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header with Orange Gradient */}
                <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
                    <div className="flex items-center justify-between px-8 py-6">
                        <div className="flex items-center space-x-4">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                                <Briefcase className="w-7 h-7 text-black" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-black">Project Profile</h2>
                                <p className="text-black-100 mt-1">Project Management System</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-grow px-8 py-6 overflow-y-auto">
                    <Section title="Basic Information" icon={<Briefcase />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DetailItem icon={<Briefcase size={20} />} label="Project Title" value={project.projectTitle} />
                            <DetailItem icon={<MapPin size={20} />} label="Location" value={project.location} />
                            <DetailItem icon={<Tag size={20} />} label="Categories" value={project.projectCategories} />
                            <DetailItem icon={<Info size={20} />} label="Status" value={project.status} />
                        </div>
                    </Section>

                    <Section title="Project Overview" icon={<FileText />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <DetailItem icon={<FileText size={20} />} label="Description" value={project.description} isLongText />
                            </div>
                            <DetailItem icon={<Calendar size={20} />} label="Start Date" value={format(new Date(project.startDate), 'dd MMM yyyy')} />
                            <DetailItem icon={<Calendar size={20} />} label="End Date" value={format(new Date(project.endDate), 'dd MMM yyyy')} />
                            <DetailItem icon={<Clock size={20} />} label="Duration" value={`${project.duration || 'N/A'} days`} />
                            <DetailItem icon={<User size={20} />} label="Program Manager" value={project.assignedProgramManager} />
                        </div>
                    </Section>

                    {/* ✨ Financial Information Section */}
                    <Section title="Financial Information" icon={<DollarSign size={20} />}>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DetailItem icon={<Tag size={20} />} label="Budget Code" value={project.budgetCode} />
                            <DetailItem icon={<DollarSign size={20} />} label="Project Budget" value={`$${Number(project.projectBudget).toLocaleString(undefined, {minimumFractionDigits: 2})}`} />
                        </div>
                    </Section>

                    {/* ✨ Financial Transaction Log Section */}
                    <Section title="Financial Transactions (Budget Code)" icon={<Clock size={20} />}>
                        {project.financialTransactions && project.financialTransactions.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-left text-sm">
                                    <thead>
                                        <tr className="bg-orange-50 text-orange-800 border-b border-orange-200">
                                            <th className="p-3 font-semibold rounded-tl-lg">Date</th>
                                            <th className="p-3 font-semibold">Type</th>
                                            <th className="p-3 font-semibold">Amount</th>
                                            <th className="p-3 font-semibold">Description</th>
                                            <th className="p-3 font-semibold rounded-tr-lg">Actor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {project.financialTransactions.map((tx, index) => (
                                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                                <td className="p-3 text-gray-600 whitespace-nowrap">
                                                    {format(new Date(tx.date), 'dd MMM yyyy')}
                                                </td>
                                                <td className="p-3">
                                                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        {tx.type}
                                                    </span>
                                                </td>
                                                <td className="p-3 font-medium text-gray-900">
                                                    ${Number(tx.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                                </td>
                                                <td className="p-3 text-gray-600 italic">
                                                    {tx.description || '-'}
                                                </td>
                                                <td className="p-3 font-medium text-gray-800">
                                                    {tx.actor ? `${tx.actor.firstName} ${tx.actor.lastName}` : 'System'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <Clock className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                                <p className="text-gray-500 text-sm">No financial transactions recorded for this project yet.</p>
                            </div>
                        )}
                    </Section>

                    {/* ✨ NEW: Activity History / Audit Log Section */}
                    <Section title="Activity History" icon={<History size={20} />}>
                        {project.auditLog && project.auditLog.length > 0 ? (
                            <div className="space-y-3">
                                {project.auditLog.map((log, index) => (
                                    <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100">
                                        <div className="flex-shrink-0 mr-3">
                                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">
                                                {log.action.charAt(0)}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <p className="text-sm font-semibold text-gray-800">
                                                    {log.action}
                                                </p>
                                                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                                    {format(new Date(log.timestamp), 'dd MMM yyyy, HH:mm')}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-600 mt-1">
                                                by <span className="font-medium">{log.actorName}</span>
                                            </p>
                                            {log.details && (
                                                <p className="text-xs text-gray-500 mt-1 italic">
                                                    {log.details}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <History className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                                <p className="text-gray-500 text-sm">No recent activity logged.</p>
                            </div>
                        )}
                    </Section>

                    <Section title="Donor Information" icon={<Users />}>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            {project.donor && project.donor.length > 0 ? (
                                <div className="space-y-4">
                                    {project.donor.map((donorItem) => (
                                        <div key={donorItem._id} className="border-b border-gray-200 last:border-b-0 pb-4">
                                            <p className="font-medium text-gray-900 flex items-center">
                                                <Users size={16} className="mr-2 text-orange-500" />
                                                {donorItem.name || donorItem.organization}
                                            </p>
                                            <p className="text-sm text-gray-600 mt-1 pl-6">
                                                Email: {donorItem.email || 'N/A'} | Phone: {donorItem.phone?.primary || 'N/A'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <Users className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-gray-500">No donors assigned to this project.</p>
                                </div>
                            )}
                        </div>
                    </Section>

                    <Section title="Audit Information" icon={<Calendar />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DetailItem icon={<User size={20} />} label="Created By" value={project.createdBy?.name || 'N/A'} />
                            <DetailItem icon={<Calendar size={20} />} label="Created At" value={project.createdAt ? format(new Date(project.createdAt), 'dd MMM yyyy') : 'N/A'} />
                            <div className="md:col-span-2">
                                <DetailItem icon={<Calendar size={20} />} label="Last Updated" value={project.updatedAt ? format(new Date(project.updatedAt), 'dd MMM yyyy') : 'N/A'} />
                            </div>
                        </div>
                    </Section>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
                    <div className="flex justify-end">
                        <button 
                            onClick={onClose} 
                            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProjectModal;